import { CoursePackageResponseDTO } from '@/jbi-shared/dto/jtms.dtos';
import { CoursePackageStatus } from '@/jbi-shared/types/jtms.types';
import { ApiState } from '@/store/types/general.types';
import { generateTempId } from '@/utils/jtms-utils';

export interface CoursePackageState {
  apiState: CoursePackageApiState;
  upsertedCoursePackage?: UpsertCoursePackageResponse;
  coursePackageTabState: CoursePackageTabState;
}

export interface CoursePackageApiState {
  upsertCoursePackageApi: ApiState;
  deleteCoursePackageApi: ApiState;
}

export interface CoursePackageTabState {
  activeCoursePackages: CoursePackageComponent[];
  coursePackageFormHasError: boolean;
}

// Frontend-specific properties, for validation and rendering
export interface CoursePackageComponent extends CoursePackageResponseDTO {
  label?: string;
  hasFieldError?: boolean;
  hasDuplicationError?: boolean;
  shouldDisabledCourseModuleDropdown?: boolean;
  shouldDisableAmountOrCurrency?: boolean;
}

export interface UpdateCoursePackageStatePayload {
  index: number;
  data: Partial<CoursePackageComponent>;
}

/**
 * Creates and returns a new, default `CoursePackageComponent` object.
 *
 * @description
 * This is a **factory function**. It is used to ensure that a fresh, independent
 * object instance is created every time it's called, preventing issues with
 * shared object references in the state.
 *
 * The `generateTempId()` function is called *inside* the factory to guarantee that
 * each new package receives a unique, temporary ID (a negative number). This is
 * essential for managing multiple new, unsaved packages in the UI (e.g., for `v-for` keys)
 * before they are persisted and receive a real ID from the database.
 *
 * The object is initialized with `hasFieldError: true` to ensure that a newly
 * added, empty package is considered invalid by default until the user fills it out.
 *
 * @returns A new object conforming to the `CoursePackageComponent` interface with a unique temporary ID.
 * @example
 * const newPackage1 = defaultCoursePackage(); // e.g., { id: -167... }
 */
export const defaultCoursePackage = (): CoursePackageComponent => ({
  id: generateTempId(),
  currency: '',
  amount: 0,
  courseModules: [],
  status: CoursePackageStatus.ACTIVE,
  hasFieldError: true,
  hasDuplicationError: false,
  shouldDisabledCourseModuleDropdown: false,
  shouldDisableAmountOrCurrency: false
});

export interface UpsertCoursePackageResponse {
  tempId?: number;
  realId: number;
  coursePackageEntity: CoursePackageResponseDTO;
}

export interface ValidationCheckPayload {
  hasModules: boolean;
  isFreeCourse: boolean;
}
